import utils from './utils.js'
import handleOption from './options'
import Events from './events.js'
import Storage from './storage.js'
import Template from './template.js'
import Bar from './bar.js'
import Controller from './controller.js'
import Timer from './timer.js'

const instances = []

class Player {
  constructor (options) {
    this.options = handleOption(options)
    this.container = this.options.container
    this.paused = true
    this.playedPromise = Promise.resolve()

    this.container.classList.add('ss-audio-player')
    if (utils.isMobile()) {
      this.container.classList.add('testv__audio__player__mobile')
    }

    this.template = new Template({
      container: this.container,
      options: this.options
    })

    this.events = new Events()
    this.storage = new Storage(this)
    this.bar = new Bar(this.template)
    this.controller = new Controller(this)
    this.timer = new Timer(this)

    this.initAudio()
    this.bindEvents()
    if (this.options.autoplay) {
      this.play()
    }

    instances.push(this)
  }

  initAudio () {
    this.audio = document.createElement('audio')
    this.audio.preload = this.options.preload

    for (let i = 0; i < this.events.audioEvents.length; i++) {
      this.audio.addEventListener(this.events.audioEvents[i], (e) => {
        this.events.trigger(this.events.audioEvents[i], e)
      })
    }

    this.volume(this.storage.get('volume'), true)
    this.setAudio(this.options.audio)
  }

  bindEvents () {
    this.on('play', () => {
      if (this.paused) {
        this.setUIPlaying()
      }
    })

    this.on('pause', () => {
      if (!this.paused) {
        this.setUIPaused()
      }
    })

    this.on('timeupdate', () => {
      if (!this.disableTimeupdate) {
        this.bar.set('played', this.audio.currentTime / this.duration, 'width')
        const currentTime = utils.secondToTime(this.audio.currentTime)
        if (this.template.ptime.innerHTML !== currentTime) {
          this.template.ptime.innerHTML = currentTime
        }
      }
    })

    this.on('durationchange', () => {
      if (this.duration !== 1) {
        this.template.dtime.innerHTML = utils.secondToTime(this.duration)
        this.controller.initNodeList()
      }
    })

    this.on('loadedmetadata', () => {
      this.seek(0)
      if (!this.paused) {
        this.audio.play()
      }
    })

    this.on('canplay', () => {
      const percentage = this.audio.buffered.length ? this.audio.buffered.end(this.audio.buffered.length - 1) / this.duration : 0
      this.bar.set('loaded', percentage, 'width')
    })

    this.on('progress', () => {
      const percentage = this.audio.buffered.length ? this.audio.buffered.end(this.audio.buffered.length - 1) / this.duration : 0
      this.bar.set('loaded', percentage, 'width')
    })

    this.on('error', () => {
      this.notice('音频加载出错！')
    })

    this.on('ended', () => {
      if (this.options.loop === 'one') {
        this.play()
      }
    })
  }

  setAudio (audio) {
    if (this.hls) {
      this.hls.destroy()
      this.hls = null
    }
    let type = audio.type
    if (this.options.customAudioType && this.options.customAudioType[type]) {
      if (Object.prototype.toString.call(this.options.customAudioType[type]) === '[object Function]') {
        this.options.customAudioType[type](this.audio, audio, this)
      } else {
        console.error(`Illegal customType: ${type}`)
      }
    } else {
      if (!type || type === 'auto') {
        if (/m3u8(#|\?|$)/i.exec(audio.url)) {
          type = 'hls'
        } else {
          type = 'normal'
        }
      }
      if (type === 'hls') {
        if (window.Hls.isSupported()) {
          this.hls = new window.Hls()
          this.hls.loadSource(audio.url)
          this.hls.attachMedia(this.audio)
        } else if (this.audio.canPlayType('application/x-mpegURL') || this.audio.canPlayType('application/vnd.apple.mpegURL')) {
          this.audio.src = audio.url
        } else {
          this.notice('Error: HLS is not supported.')
        }
      } else if (type === 'normal') {
        this.audio.src = audio.url
      }
    }
  }

  seek (time) {
    time = Math.max(time, 0)
    time = Math.min(time, this.duration)
    this.audio.currentTime = time
    this.bar.set('played', time / this.duration, 'width')
    this.template.ptime.innerHTML = utils.secondToTime(time)
  }

  get duration () {
    return isNaN(this.audio.duration) ? 0 : this.audio.duration
  }

  setUIPlaying () {
    if (this.paused) {
      this.paused = false
      this.template.button.classList.add('playing')
    }

    this.timer.enable('loading')

    if (this.options.mutex) {
      for (let i = 0; i < instances.length; i++) {
        if (this !== instances[i]) {
          instances[i].pause()
        }
      }
    }
  }

  play () {
    this.setUIPlaying()

    const playPromise = this.audio.play()
    if (playPromise) {
      playPromise.catch((e) => {
        console.warn(e)
        if (e.name === 'NotAllowedError') {
          this.setUIPaused()
        }
      })
    }
  }

  setUIPaused () {
    if (!this.paused) {
      this.paused = true
      this.template.button.classList.remove('playing')
    }

    this.container.classList.remove('testv__audio__player__loading')
    this.timer.disable('loading')
  }

  pause () {
    this.setUIPaused()
    this.audio.pause()
  }

  switchVolumeIcon () {
    if (this.volume() > 0) {
      this.template.volumeButton.classList.remove('off')
    } else {
      this.template.volumeButton.classList.add('off')
    }
  }

  volume (percentage, nostorage) {
    percentage = parseFloat(percentage)
    if (!isNaN(percentage)) {
      percentage = Math.max(percentage, 0)
      percentage = Math.min(percentage, 1)
      this.bar.set('volume', percentage, 'width')
      if (!nostorage) {
        this.storage.set('volume', percentage)
      }

      this.audio.volume = percentage
      if (this.audio.muted) {
        this.audio.muted = false
      }

      this.switchVolumeIcon()
    }

    return this.audio.muted ? 0 : this.audio.volume
  }

  speed (rate) {
    this.audio.playbackRate = rate
  }

  on (name, callback) {
    this.events.on(name, callback)
  }

  toggle () {
    if (this.template.button.classList.contains('playing')) {
      this.pause()
    } else {
      this.play()
    }
  }

  destroy () {
    instances.splice(instances.indexOf(this), 1)
    this.pause()
    this.container.innerHTML = ''
    this.audio.src = ''
    this.timer.destroy()
    this.events.trigger('destroy')
  }
}

export default Player
